import { ModelBase } from '../abstract/model-base.model';
import { Currency } from '../core/currency.model';
import { PaymentMethod } from '../core/payment-method.model';
import { PaymentTerm } from '../invoicing/payment-term.model';
import { SalesDocumentTemplate } from '../sales/template.model';
import { FkField } from '../../decorators/api-field.decorator';

/**
 * Réglages de vente propres à un client (#1114) : sous-objet un-à-un de la fiche
 * partenaire, écrit par le `PUT` de la fiche. Chaque champ surcharge le réglage de même
 * nom des paramètres de vente du dossier ; **vide = hérite** (`null`, chaîne vide, liste
 * vide). Les pièces jointes d'envoi ne figurent pas ici : elles se lisent par les
 * navigations `quotation-attachments` / `invoice-attachments` / `credit-note-attachments`
 * de `PartnerService`.
 */
export class PartnerSalesSettings extends ModelBase {

    static override CLASS_NAME: string = 'PartnerSalesSettings';

    private _bankDetails!: string;
    private _chequeLabel!: string;
    private _creditNoteEmailBody!: string;
    private _creditNoteEmailSubject!: string;
    private _creditNoteGeneralConditions!: string;
    private _creditNoteTemplate!: SalesDocumentTemplate | null;
    private _currency!: Currency | null;
    private _invoiceEmailBody!: string;
    private _invoiceEmailSubject!: string;
    private _invoiceGeneralConditions!: string;
    private _invoicePaymentTerm!: PaymentTerm | null;
    private _invoiceReminderEmailBody!: string;
    private _invoiceReminderEmailSubject!: string;
    private _invoiceTemplate!: SalesDocumentTemplate | null;
    private _paymentMethods!: PaymentMethod[];
    private _quotationAcceptanceConditions!: string;
    private _quotationAddendumFastForward!: boolean | null;
    private _quotationEmailBody!: string;
    private _quotationEmailSubject!: string;
    private _quotationReminderEmailBody!: string;
    private _quotationReminderEmailSubject!: string;
    private _quotationTemplate!: SalesDocumentTemplate | null;
    private _quotationValidityPeriod!: number | null;

    public get bankDetails(): string {
        return this._bankDetails;
    }

    public set bankDetails(value: any) {
        this._setValue('_bankDetails', value, 'string');
    }

    public get chequeLabel(): string {
        return this._chequeLabel;
    }

    public set chequeLabel(value: any) {
        this._setValue('_chequeLabel', value, 'string');
    }

    public get creditNoteEmailBody(): string {
        return this._creditNoteEmailBody;
    }

    public set creditNoteEmailBody(value: any) {
        this._setValue('_creditNoteEmailBody', value, 'string');
    }

    public get creditNoteEmailSubject(): string {
        return this._creditNoteEmailSubject;
    }

    public set creditNoteEmailSubject(value: any) {
        this._setValue('_creditNoteEmailSubject', value, 'string');
    }

    public get creditNoteGeneralConditions(): string {
        return this._creditNoteGeneralConditions;
    }

    public set creditNoteGeneralConditions(value: any) {
        this._setValue('_creditNoteGeneralConditions', value, 'string');
    }

    @FkField('id')
    public get creditNoteTemplate(): SalesDocumentTemplate | null {
        return this._creditNoteTemplate;
    }

    public set creditNoteTemplate(value: any) {
        this._setValue('_creditNoteTemplate', value, 'SalesDocumentTemplate');
    }

    @FkField('code')
    public get currency(): Currency | null {
        return this._currency;
    }

    public set currency(value: any) {
        this._setValue('_currency', value, 'Currency');
    }

    public get invoiceEmailBody(): string {
        return this._invoiceEmailBody;
    }

    public set invoiceEmailBody(value: any) {
        this._setValue('_invoiceEmailBody', value, 'string');
    }

    public get invoiceEmailSubject(): string {
        return this._invoiceEmailSubject;
    }

    public set invoiceEmailSubject(value: any) {
        this._setValue('_invoiceEmailSubject', value, 'string');
    }

    public get invoiceGeneralConditions(): string {
        return this._invoiceGeneralConditions;
    }

    public set invoiceGeneralConditions(value: any) {
        this._setValue('_invoiceGeneralConditions', value, 'string');
    }

    @FkField('id')
    public get invoicePaymentTerm(): PaymentTerm | null {
        return this._invoicePaymentTerm;
    }

    public set invoicePaymentTerm(value: any) {
        this._setValue('_invoicePaymentTerm', value, 'PaymentTerm');
    }

    public get invoiceReminderEmailBody(): string {
        return this._invoiceReminderEmailBody;
    }

    public set invoiceReminderEmailBody(value: any) {
        this._setValue('_invoiceReminderEmailBody', value, 'string');
    }

    public get invoiceReminderEmailSubject(): string {
        return this._invoiceReminderEmailSubject;
    }

    public set invoiceReminderEmailSubject(value: any) {
        this._setValue('_invoiceReminderEmailSubject', value, 'string');
    }

    @FkField('id')
    public get invoiceTemplate(): SalesDocumentTemplate | null {
        return this._invoiceTemplate;
    }

    public set invoiceTemplate(value: any) {
        this._setValue('_invoiceTemplate', value, 'SalesDocumentTemplate');
    }

    @FkField('code')
    public get paymentMethods(): PaymentMethod[] {
        return this._paymentMethods;
    }

    public set paymentMethods(value: any) {
        this._setValue('_paymentMethods', value, 'PaymentMethod');
    }

    public get quotationAcceptanceConditions(): string {
        return this._quotationAcceptanceConditions;
    }

    public set quotationAcceptanceConditions(value: any) {
        this._setValue('_quotationAcceptanceConditions', value, 'string');
    }

    /**
     * Avis du client sur l'acceptation automatique des avenants (#1001) : deuxième
     * niveau de la cascade. `null` = ce client ne tranche pas, le paramétrage des
     * ventes s'applique.
     *
     * ⚠️ **Ne pas passer par `_setValue(..., 'boolean')`** : `toBoolean()` ramène
     * `null` à `false`, ce qui transformerait silencieusement « hérité » en « non »
     * et figerait le client sur un choix qu'il n'a pas fait.
     */
    public get quotationAddendumFastForward(): boolean | null {
        return this._quotationAddendumFastForward;
    }

    public set quotationAddendumFastForward(value: any) {
        this._quotationAddendumFastForward = value === null || value === undefined
            ? null
            : value === true || value === 1 || value === '1';
    }

    public get quotationEmailBody(): string {
        return this._quotationEmailBody;
    }

    public set quotationEmailBody(value: any) {
        this._setValue('_quotationEmailBody', value, 'string');
    }

    public get quotationEmailSubject(): string {
        return this._quotationEmailSubject;
    }

    public set quotationEmailSubject(value: any) {
        this._setValue('_quotationEmailSubject', value, 'string');
    }

    public get quotationReminderEmailBody(): string {
        return this._quotationReminderEmailBody;
    }

    public set quotationReminderEmailBody(value: any) {
        this._setValue('_quotationReminderEmailBody', value, 'string');
    }

    public get quotationReminderEmailSubject(): string {
        return this._quotationReminderEmailSubject;
    }

    public set quotationReminderEmailSubject(value: any) {
        this._setValue('_quotationReminderEmailSubject', value, 'string');
    }

    @FkField('id')
    public get quotationTemplate(): SalesDocumentTemplate | null {
        return this._quotationTemplate;
    }

    public set quotationTemplate(value: any) {
        this._setValue('_quotationTemplate', value, 'SalesDocumentTemplate');
    }

    /**
     * Durée de validité des devis en jours. `null` = hérite ; on n'emprunte pas
     * `_setValue(..., 'integer')`, qui ramènerait un champ vidé à `0`.
     */
    public get quotationValidityPeriod(): number | null {
        return this._quotationValidityPeriod;
    }

    public set quotationValidityPeriod(value: any) {
        this._quotationValidityPeriod = value === null || value === undefined || value === ''
            ? null
            : parseInt(value, 10);
    }

}
